import { useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector as useSelector } from "../../../../store";
import Chevron from "../../../UI/Icons/Chevron";
import Search from "../../../UI/Icons/Search";
import Roles from "../../../../models/roles";

const CentersFilter = ({
  selectedCentreIds,
  setSelectedCentreIds,
}: {
  selectedCentreIds: number[];
  setSelectedCentreIds: (ids: number[]) => void;
}) => {
  const user = useSelector((state) => state.auth.user);
  const isCandidate = useMemo(
    () => user?.role.name === Roles.CANDIDATE,
    [user]
  );
  const allCentersList = useSelector((state) =>
    isCandidate
      ? state.candidateJobs.candidateCentres
      : state.timesheet.allCentersList
  );
  const [openDropdown, setOpenDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectAllRef = useRef<HTMLInputElement | null>(null);

  const filteredCenters = allCentersList.filter((item) =>
    item?.business_name !== null ? item?.business_name.toLowerCase().includes(searchTerm.toLowerCase()) : ""
  );

  const allIds = useMemo(
    () => allCentersList.map((c) => c.id),
    [allCentersList]
  );
  const isAllSelected = selectedCentreIds.length === allIds.length;
  const isIndeterminate = selectedCentreIds.length > 0 && !isAllSelected;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keep indeterminate state in sync
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const toggleCentre = (id: number) => {
    const exists = selectedCentreIds.includes(id);
    const newSelected = exists
      ? selectedCentreIds.filter((i) => i !== id)
      : [...selectedCentreIds, id];
    setSelectedCentreIds(newSelected);
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedCentreIds([]);
    } else {
      setSelectedCentreIds(allIds);
    }
  };

  const getLabel = () => {
    if (isAllSelected) return "All";
    if (selectedCentreIds.length === 0) return "All";
    return `${selectedCentreIds.length} Selected`;
  };

  return (
    <>
      {allCentersList.length > 0 && (
        <>
          {/* Toggle Dropdown */}
          <div
            className="d-flex align-items-center gap-3 cursor-pointer"
            onClick={() => setOpenDropdown((prev) => !prev)}
          >
            <div className="fs-13 fw-700 text-black">{getLabel()}</div>
            <Chevron
              size={16}
              isOpen={openDropdown}
              // fill={openDropdown ? "black" : "#AAA"}
            />
          </div>

          {openDropdown && (
            <div
              className="position-absolute top-100 end-0 z-10 bg-teal-pale border border-black shadow-md w-266px rounded z-1"
              ref={dropdownRef}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Search */}
              <div className=" p-6px">
                <div className="form-group w-100 input-icon icon-left">
                  <Search />
                  <input
                    type="text"
                    className="form-control fs-16 fw-400"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              {/* Select All */}
              <div
                className="px-15px py-8px d-flex align-items-center hover-bg-off-white"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.preventDefault();
                  handleSelectAll();
                }}
              >
                <div className="theme-check">
                  <input
                    type="checkbox"
                    className="theme-check-input"
                    id="selectAll"
                    readOnly
                    checked={isAllSelected}
                    ref={selectAllRef}
                  />
                  <label
                    htmlFor="selectAll"
                    className="theme-check-label lh-24px fw-700 fs-16"
                  >
                    Select/Deselect All
                  </label>
                </div>
              </div>

              {/* List */}
              <div className="max-h-310px overflow-auto">
                {filteredCenters.length > 0 ? (
                  filteredCenters.map((centre) => (
                    <div
                      key={centre.id}
                      className="border-top border-black px-15px py-8px d-flex align-items-center hover-bg-off-white text-break"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.preventDefault();
                        toggleCentre(centre.id);
                      }}
                    >
                      <div className="theme-check">
                        <input
                          type="checkbox"
                          className="theme-check-input"
                          id={`centre-${centre.id}`}
                          readOnly
                          checked={selectedCentreIds.includes(centre.id)}
                        />
                        <label
                          htmlFor={`centre-${centre.id}`}
                          className="theme-check-label lh-24px fs-16 fw-400"
                        >
                          {centre.business_name}
                        </label>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-grey-dark py-2">
                    No centres found
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default CentersFilter;
